import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Grid, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Chip, IconButton, InputBase, Tooltip, Menu, MenuItem, FormControl, Select, InputLabel, CircularProgress, Popover } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import DownloadIcon from "@mui/icons-material/Download";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import color from "../../../constants/color";
import styles from "./style";
import { formatDate } from "../../../utils/DateFormat";
import PaymentStatusModal from "../../../modals/PaymentStatusModal";
import CustomPagination from "../../../components/CustomPagination";
import { ReactComponent as SearchIcon } from "../../../assets/images/searchIcon.svg";
import Modal from "../../../components/Modal";
import { getRegionsByCountry } from "../../../utils/regions";
import { planService } from "../../../api";
import countryList from "react-select-country-list";

const UserManagementPage = ({
  clients = [],
  page = 1,
  total = 0,
  setPage = () => { },
  setRowsPerPage = () => { },
  rowsPerPage = 10,
  searchText = "",
  onSearchChange = () => { },
  onToggleBlock = () => { },
  toggleModalOpen = false,
  toggleTargetClient = null,
  onCloseToggleModal = () => { },
  onConfirmToggleModal = () => { },
  metrics = {},
  loading = false,
  onExport,
  paymentModalOpen = false,
  paymentModalStatus = "success",
  paymentModalTitle = "",
  paymentModalMessage = "",
  paymentReference = "",
  onClosePaymentModal = () => { },
  deleteModalOpen = false,
  deleteTargetClient = null,
  onDeleteClient = () => { },
  onCloseDeleteModal = () => { },
  onConfirmDeleteModal = () => { },
  deleteLoading = false,
  statusFilter = "All",
  onStatusFilterChange = () => { },
  onApplyBusinessFilters = () => { },
  onResetBusinessFilters = () => { },
}) => {
  const businessFilterLabels = {
    country: "Country",
    region: "Region",
    package: "Package",
    currency: "Currency",
  };
  const emptyBusinessFilters = {
    country: "",
    region: "",
    package: "",
    currency: ""
  };
  const navigate = useNavigate();
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const statusFilterMenuOpen = Boolean(statusFilterAnchorEl);
  const [businessFilterAnchorEl, setBusinessFilterAnchorEl] = useState(null);
  const [activeBusinessFilter, setActiveBusinessFilter] = useState("country");
  const businessFilterMenuOpen = Boolean(businessFilterAnchorEl);

  const [businessFilters, setBusinessFilters] = useState(emptyBusinessFilters);
  const [appliedBusinessFilters, setAppliedBusinessFilters] = useState(emptyBusinessFilters);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [regionsLoading, setRegionsLoading] = useState(false);

  const currencies = ["USD", "EUR", "GBP", "INR", "ZAR"];

  useEffect(() => {
    loadCountries();
    loadPackages();
  }, []);

  useEffect(() => {
    if (businessFilters.country) {
      loadRegions(businessFilters.country);
    } else {
      setRegions([]);
      setBusinessFilters(prev => ({ ...prev, region: "" }));
    }
  }, [businessFilters.country]);

  const loadCountries = async () => {
    try {
      const countryOptions = countryList().getData();
      const countryNames = countryOptions.map(option => option.label);
      setCountries(countryNames);
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };

  const loadRegions = async (countryName) => {
    setRegionsLoading(true);
    try {
      const regionList = await getRegionsByCountry(countryName);
      setRegions(regionList);
    } catch (error) {
      console.error("Error loading regions:", error);
      setRegions([]);
    } finally {
      setRegionsLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      const result = await planService.list();
      const plans = Array.isArray(result) ? result : result?.data || [];
      setPackages(plans);
    } catch (error) {
      console.error("Error loading packages:", error);
      setPackages([]);
    }
  };

  const handleBusinessFilterChange = (field, value) => {
    setBusinessFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field === "country" ? { region: "" } : {}),
    }));
  };

  const handleBusinessFilterMenuClick = (event) => {
    setBusinessFilters({ ...appliedBusinessFilters });
    setActiveBusinessFilter("country");
    setBusinessFilterAnchorEl(event.currentTarget);
  };

  const handleBusinessFilterMenuClose = () => {
    setBusinessFilterAnchorEl(null);
    setActiveBusinessFilter("country");
  };

  const handleBusinessSubmenuOpen = (field) => {
    setActiveBusinessFilter(field);
  };

  const handleApplyFilters = async (nextFilters = businessFilters) => {
    const filtersToApply = {
      ...emptyBusinessFilters,
      ...nextFilters,
      ...(nextFilters.country ? {} : { region: "" }),
    };

    setFiltersLoading(true);
    try {
      await onApplyBusinessFilters(filtersToApply);
      setBusinessFilters(filtersToApply);
      setAppliedBusinessFilters(filtersToApply);
      handleBusinessFilterMenuClose();
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleRemoveBusinessFilter = async (field) => {
    const nextFilters = {
      ...appliedBusinessFilters,
      [field]: "",
      ...(field === "country" ? { region: "" } : {}),
    };
    setBusinessFilters(nextFilters);
    setAppliedBusinessFilters(nextFilters);
    if (field === "country") {
      setRegions([]);
    }
    setFiltersLoading(true);
    try {
      await onApplyBusinessFilters(nextFilters);
    } catch (error) {
      console.error("Error removing filter:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleResetFilters = async () => {
    const resetFilters = { ...emptyBusinessFilters };
    setBusinessFilters(resetFilters);
    setAppliedBusinessFilters(resetFilters);
    setRegions([]);
    setFiltersLoading(true);
    try {
      await onResetBusinessFilters();
      handleBusinessFilterMenuClose();
    } catch (error) {
      console.error("Error resetting filters:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const hasActiveFilters = Object.values(appliedBusinessFilters).some(value => value !== "");
  const businessFilterOptions = {
    country: countries,
    region: regions,
    package: packages
      .map((pkg) => ({
        label: pkg.planName,
        value: pkg._id || pkg.id || pkg.planName,
      }))
      .filter((pkg) => pkg.label && pkg.value),
    currency: currencies,
  };
  const activeFilterLabel = businessFilterLabels[activeBusinessFilter] || "";
  const activeFilterOptions = (businessFilterOptions[activeBusinessFilter] || []).map((option) => (
    typeof option === "string" ? { label: option, value: option } : option
  ));

  const getBusinessFilterChipLabel = (field, value) => {
    if (field !== "package") return value;

    const selectedPackage = packages.find((pkg) => (
      (pkg._id || pkg.id || pkg.planName) === value
    ));

    return selectedPackage?.planName || value;
  };

  const handleAddNewClient = () => {
    navigate("/dashboard/userManagement/createClient");
  };

  const handleViewClient = (client) => {
    navigate(`/dashboard/userManagement/viewClient/${client.id}`);
  };

  const handleEditClient = (client) => {
    navigate(`/dashboard/userManagement/edit/${client.id}`);
  };

  const handleFilterClick = (event) => {
    setStatusFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setStatusFilterAnchorEl(null);
  };

  const handleFilterSelect = (status) => {
    onStatusFilterChange(status);
    handleFilterClose();
  };

  const NEGATIVE_STATUSES = ["expired", "blocked", "inactive"];
  const isNegativeStatus = (client) => {
    const statusText = String(client?.status || "").toLowerCase();
    if (statusText) return NEGATIVE_STATUSES.includes(statusText);
    return client?.isActive === false;
  };

  const getStatusTooltipText = (client) => {
    const modifiedDate = client?.subscription?.statusModifiedAt;
    if (!modifiedDate) return "No modification date available";

    const formattedDate = formatDate(modifiedDate);
    return `Modified on - ${formattedDate}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>Owner Dashboard</Typography>
        <Button
          variant="contained"
          onClick={handleAddNewClient}
          sx={styles.addButton}
        >
          + Add New Client
        </Button>
      </Box>

      {/* Business Report Filters */}
      <Box sx={styles.filtersCard}>
        <Box sx={styles.businessReportsHeader}>
          <Typography sx={styles.filtersTitle}>Business Reports</Typography>
          <Button
            variant="outlined"
            onClick={handleBusinessFilterMenuClick}
            disabled={filtersLoading}
            sx={styles.businessFilterButton}
            startIcon={filtersLoading ? <CircularProgress size={16} /> : <FilterListIcon />}
            endIcon={<KeyboardArrowDownIcon />}
          >
            Filter
          </Button>
        </Box>
        {hasActiveFilters && (
          <Box sx={styles.filterChips}>
            {Object.entries(appliedBusinessFilters).map(([field, value]) => (
              value ? (
                <Chip
                  key={field}
                  label={getBusinessFilterChipLabel(field, value)}
                  onDelete={() => handleRemoveBusinessFilter(field)}
                  sx={styles.filterChip}
                />
              ) : null
            ))}
          </Box>
        )}
        <Popover
          open={businessFilterMenuOpen}
          anchorEl={businessFilterAnchorEl}
          onClose={handleBusinessFilterMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{ sx: styles.businessFilterPopover }}
        >
          <Box sx={styles.businessFilterContent}>
            <Box sx={styles.businessFilterNav}>
              {Object.entries(businessFilterLabels).map(([field, label]) => (
                <MenuItem
                  key={field}
                  onClick={() => handleBusinessSubmenuOpen(field)}
                  selected={activeBusinessFilter === field}
                  sx={styles.businessFilterMenuItem}
                >
                  <span>{label}</span>
                  <ChevronRightIcon fontSize="small" />
                </MenuItem>
              ))}
            </Box>

            <Box sx={styles.businessFilterPanel}>
              <Typography sx={styles.submenuTitle}>{activeFilterLabel}</Typography>
              <FormControl fullWidth size="small">
                <InputLabel sx={styles.filterLabel}>{`Select ${activeFilterLabel}`}</InputLabel>
                <Select
                  value={businessFilters[activeBusinessFilter] || ""}
                  onChange={(e) => handleBusinessFilterChange(activeBusinessFilter, e.target.value)}
                  label={`Select ${activeFilterLabel}`}
                  disabled={activeBusinessFilter === "region" && (!businessFilters.country || regionsLoading)}
                  sx={styles.filterSelect}
                >
                  <MenuItem value="">
                    <em>{`All ${activeFilterLabel || "Options"}`}</em>
                  </MenuItem>
                  {activeBusinessFilter === "region" && regionsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading...
                    </MenuItem>
                  ) : (
                    activeFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Box sx={styles.submenuActions}>
                {hasActiveFilters && (
                  <Button
                    variant="text"
                    onClick={handleResetFilters}
                    disabled={filtersLoading}
                    sx={styles.clearFiltersButton}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => handleApplyFilters()}
                  disabled={filtersLoading}
                  sx={styles.applyButton}
                  startIcon={filtersLoading ? <CircularProgress size={16} /> : null}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={2} sx={styles.metricsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={styles.metricCard}>
            <Typography sx={styles.metricLabel}>Total Clients</Typography>
            <Typography sx={styles.metricValue}>{metrics.totalClients || 0}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={styles.metricCard}>
            <Typography sx={styles.metricLabel}>Active Subscriptions</Typography>
            <Typography sx={styles.metricValue}>{metrics.activeSubscriptions || 0}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={styles.metricCard}>
            <Typography sx={styles.metricLabel}>Monthly Revenue</Typography>
            <Typography sx={styles.metricValue}>{metrics.monthlyRevenue || "$0"}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={styles.metricCard}>
            <Typography sx={styles.metricLabel}>Payslips Used (MTD)</Typography>
            <Typography sx={styles.metricValue}>{metrics.payslipsUsed || "0"}</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Clients Table Section */}
      <Box sx={styles.tableSection}>
        <Box sx={styles.tableHeader}>
          <Box sx={styles.tableHeaderLeft}>
            <Typography sx={styles.sectionTitle}>Clients</Typography>
            <Box sx={styles.searchBox}>
              <IconButton type="button" sx={styles.searchIconButton}>
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={styles.searchInput}
                placeholder="Search"
                value={searchText}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              sx={styles.filterButton}
              onClick={handleFilterClick}
              disabled={loading}
            >
              Filter By {statusFilter !== "All" && `: ${statusFilter}`}
            </Button>
            <Menu
              anchorEl={statusFilterAnchorEl}
              open={statusFilterMenuOpen}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => handleFilterSelect("All")}
                selected={statusFilter === "All"}
              >
                All
              </MenuItem>
              <MenuItem
                onClick={() => handleFilterSelect("active")}
                selected={statusFilter === "active"}
              >
                Active
              </MenuItem>
              <MenuItem
                onClick={() => handleFilterSelect("inactive")}
                selected={statusFilter === "inactive"}
              >
                Inactive
              </MenuItem>
              <MenuItem
                onClick={() => handleFilterSelect("blocked")}
                selected={statusFilter === "blocked"}
              >
                Blocked
              </MenuItem>
              <MenuItem
                onClick={() => handleFilterSelect("trial")}
                selected={statusFilter === "trial"}
              >
                Trial
              </MenuItem>
              <MenuItem
                onClick={() => handleFilterSelect("expired")}
                selected={statusFilter === "expired"}
              >
                Expired
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={styles.exportButton}
              onClick={onExport}
              disabled={loading}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.tableHeaderCell}>Company</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Country</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Current Package</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Package Balance</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Join Date</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Last Login</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Last Activity</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Status</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Employees</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Payslips Used</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Next Billing</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={styles.emptyCell}>
                    <Typography>No clients found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client, index) => (
                  <TableRow
                    key={client.id || index}
                    sx={{
                      ...styles.tableRow,
                      ...(client.isActive === false
                        ? styles.blockedRow
                        : {}),
                    }}
                  >
                    {/* Company */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.company || "N/A"}
                    </TableCell>

                    {/* Country */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.country || "N/A"}
                    </TableCell>
                    {/* Current Package */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.currentPackage || "-"}
                    </TableCell>

                    {/* Package Balance */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.packageBalance ?? 0}
                    </TableCell>

                    {/* Join Date */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {formatDateTime(client.joinDate)}
                    </TableCell>

                    {/* Last Login */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {formatDateTime(client.lastLogin)}
                    </TableCell>

                    {/* Last Activity */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {formatDateTime(client.lastActivity)}
                    </TableCell>

                    {/* Status */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={
                            client.status ||
                            (client.isActive ? "Active" : "Blocked")
                          }
                          sx={{
                            ...styles.statusChip,
                            backgroundColor: isNegativeStatus(client)
                              ? "#FCEBEB"
                              : color.lightGreen,
                            color: isNegativeStatus(client)
                              ? "#C62828"
                              : color.primary,
                          }}
                        />

                        <Tooltip
                          title={getStatusTooltipText(client)}
                          arrow
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: "16px",
                              color: color.lightText,
                              cursor: "pointer",
                              ml: 0.5,
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Employees */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.employees || "0 / 0"}
                    </TableCell>

                    {/* Payslips */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.payslipsUsed || "0 / 0"}
                    </TableCell>

                    {/* Next Billing */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      {client.nextBilling || "N/A"}
                    </TableCell>

                    {/* Actions */}
                    <TableCell
                      sx={{
                        ...styles.tableCell,
                        ...(client.isActive === false
                          ? styles.blockedTableCell
                          : {}),
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleViewClient(client)}
                          sx={styles.viewButton}
                          size="small"
                        >
                          <RemoveRedEyeOutlinedIcon sx={styles.viewIcon} />
                        </IconButton>

                        <IconButton
                          onClick={() => handleEditClient(client)}
                          sx={styles.editButton}
                          size="small"
                        >
                          <EditIcon sx={styles.editIcon} />
                        </IconButton>

                        <Tooltip
                          title={
                            client.isActive
                              ? "Block Client"
                              : "Unblock Client"
                          }
                        >
                          <BlockOutlinedIcon
                            sx={{
                              color: client.isActive
                                ? "#ff0000"
                                : color.primary,
                              cursor: "pointer",
                              mt: "4px",
                            }}
                            fontSize="small"
                            onClick={() => onToggleBlock(client)}
                          />
                        </Tooltip>

                        <Tooltip title="Delete Client">
                          <DeleteOutlineIcon
                            sx={{
                              color: "#ff0000",
                              cursor: "pointer",
                              mt: "4px",
                            }}
                            fontSize="small"
                            onClick={() => onDeleteClient(client)}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CustomPagination
          pageCount={total}
          page={page}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          totalItems={clients.length}
        />
      </Box>

      <PaymentStatusModal
        open={paymentModalOpen}
        status={paymentModalStatus}
        title={paymentModalTitle}
        message={paymentModalMessage}
        reference={paymentReference}
        onClose={onClosePaymentModal}
      />

      <Modal
        open={toggleModalOpen}
        setOpen={onCloseToggleModal}
        title={toggleTargetClient?.isActive ? "Block Client" : "Unblock Client"}
        deletetText={toggleTargetClient?.isActive ? "Block" : "Unblock"}
        cancelText="Cancel"
        handleDelete={onConfirmToggleModal}
      >
        <Box>
          <Typography>
            {toggleTargetClient?.isActive
              ? "Are you sure you want to block this client?"
              : "Are you sure you want to unblock this client?"}
          </Typography>
        </Box>
      </Modal>

      <Modal
        open={deleteModalOpen}
        setOpen={onCloseDeleteModal}
        title="Delete Client"
        deletetText="Delete"
        cancelText="Cancel"
        handleDelete={onConfirmDeleteModal}
        loading={deleteLoading}
      >
        <Box>
          <Typography>Are you sure you want to delete this client?</Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserManagementPage;
